import React from 'react';
import { DollarSign, AlertTriangle, CheckCircle, TrendingDown } from 'lucide-react';

const StatBlock = ({ label, value, sub, color = 'text-foreground' }) => (
  <div className="bg-muted/30 rounded-lg p-3 flex flex-col gap-0.5">
    <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</span>
    <span className={`text-lg font-bold tabular-nums ${color}`}>{value}</span>
    {sub && <span className="text-[10px] text-muted-foreground">{sub}</span>}
  </div>
);

const fmt = (v) => {
  if (v == null) return '—';
  const abs = Math.abs(v).toFixed(2);
  return v >= 0 ? `+$${abs}` : `-$${abs}`;
};

const CostOfBreakingRules = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-4 animate-pulse">
        <div className="h-4 bg-muted rounded w-1/3 mb-4" />
        <div className="grid grid-cols-2 gap-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-muted rounded" />)}
        </div>
      </div>
    );
  }

  if (!data || data.total_trades === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-4">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-muted-foreground">Cost of Breaking Rules</h3>
        </div>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <DollarSign className="w-8 h-8 text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">No data yet.</p>
        </div>
      </div>
    );
  }

  const { clean, dirty, opportunity_cost } = data;
  const hasDirty = dirty.count > 0;
  const hasClean = clean.count > 0;

  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="flex items-center gap-2 mb-1">
        <DollarSign className="w-4 h-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-muted-foreground">Cost of Breaking Rules</h3>
      </div>
      <p className="text-xs text-muted-foreground/60 mb-4">
        Clean trades (no violations) vs dirty trades (1+ violations)
      </p>

      {/* Opportunity cost banner */}
      {hasDirty && hasClean && (
        <div className={`flex items-center gap-2 rounded-lg px-3 py-2 mb-4 ${
          opportunity_cost > 0 ? 'bg-red-500/10 border border-red-500/20' : 'bg-green-500/10 border border-green-500/20'
        }`}>
          {opportunity_cost > 0 ? (
            <TrendingDown className="w-4 h-4 text-red-400 shrink-0" />
          ) : (
            <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
          )}
          <div>
            <p className="text-xs font-semibold text-foreground">
              {opportunity_cost > 0
                ? `Estimated ${fmt(opportunity_cost)} left on the table from rule breaks`
                : 'Rule-breaking trades are not dragging you down'}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              If your {dirty.count} dirty trade{dirty.count !== 1 ? 's' : ''} had matched the clean avg of {fmt(clean.avg_pnl)}
            </p>
          </div>
        </div>
      )}

      {/* Two-column stat grid */}
      <div className="grid grid-cols-2 gap-4">
        {/* Clean trades */}
        <div>
          <div className="flex items-center gap-1 mb-2">
            <CheckCircle className="w-3 h-3 text-green-400" />
            <span className="text-xs font-semibold text-green-400">Clean Trades</span>
            <span className="text-[10px] text-muted-foreground ml-auto">{clean.count} trades</span>
          </div>
          <div className="space-y-2">
            <StatBlock
              label="Total PnL"
              value={fmt(clean.total_pnl)}
              color={clean.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}
            />
            <StatBlock
              label="Avg PnL"
              value={fmt(clean.avg_pnl)}
              color={clean.avg_pnl >= 0 ? 'text-green-400' : 'text-red-400'}
            />
            <StatBlock
              label="Win Rate"
              value={`${clean.win_rate.toFixed(1)}%`}
              color={clean.win_rate >= 50 ? 'text-green-400' : 'text-red-400'}
            />
          </div>
        </div>

        {/* Dirty trades */}
        <div>
          <div className="flex items-center gap-1 mb-2">
            <AlertTriangle className="w-3 h-3 text-red-400" />
            <span className="text-xs font-semibold text-red-400">Dirty Trades</span>
            <span className="text-[10px] text-muted-foreground ml-auto">{dirty.count} trades</span>
          </div>
          {hasDirty ? (
            <div className="space-y-2">
              <StatBlock
                label="Total PnL"
                value={fmt(dirty.total_pnl)}
                color={dirty.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}
              />
              <StatBlock
                label="Avg PnL"
                value={fmt(dirty.avg_pnl)}
                color={dirty.avg_pnl >= 0 ? 'text-green-400' : 'text-red-400'}
              />
              <StatBlock
                label="Win Rate"
                value={`${dirty.win_rate.toFixed(1)}%`}
                color={dirty.win_rate >= 50 ? 'text-green-400' : 'text-red-400'}
              />
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center py-4 text-center">
              <CheckCircle className="w-6 h-6 text-green-400/50 mb-1" />
              <p className="text-xs text-muted-foreground">No rule violations!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CostOfBreakingRules;
